import { useProviderRepositories } from '@/hooks'
import type { ConnectorType } from '@/types'
import { Button } from '@/components/ui/button'
import { ExternalLink, Lock, Unlock } from 'lucide-react'

interface RepositoryPickerProps {
  projectId: string
  connectorType: ConnectorType
  onConnect?: () => void
}

export function RepositoryPicker({ projectId, connectorType, onConnect }: RepositoryPickerProps) {
  const { data: repositories = [], isLoading, isError, error } = useProviderRepositories(projectId, connectorType, true)

  if (isLoading) {
    return <div role="status" aria-live="polite" className="text-sm text-muted-foreground">Loading repositories...</div>
  }

  if (isError) {
    const isUnauthorized = (error as any)?.response?.data?.code === 'CONNECTOR_UNAUTHORIZED'

    if (isUnauthorized) {
      return (
        <div role="alert" className="flex items-center gap-3">
          <p className="text-sm text-muted-foreground">Your token has expired or been revoked.</p>
          {onConnect && (
            <Button variant="outline" size="sm" onClick={onConnect}>
              Reconnect
            </Button>
          )}
        </div>
      )
    }

    return <div role="alert" className="text-sm text-danger-foreground">Failed to load repositories.</div>
  }

  if (repositories.length === 0) {
    return <div className="text-sm text-muted-foreground">No repositories found.</div>
  }

  return (
    <div className="max-h-60 overflow-y-auto rounded-md border">
      {repositories.map((repo) => (
        <div
          key={repo.id}
          className="flex items-center justify-between border-b px-3 py-2 last:border-b-0 hover:bg-muted/50"
        >
          <div className="flex items-center gap-2 min-w-0">
            {repo.isPrivate ? (
              <Lock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            ) : (
              <Unlock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            )}
            <span className="text-sm truncate">{repo.fullName}</span>
          </div>
          {repo.url && (
            <a
              href={repo.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground shrink-0 ml-2"
              aria-label={`Open ${repo.fullName} in new window`}
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      ))}
    </div>
  )
}
